import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { apiFetch } from "../../services/api";
import ReportBuilderWizard from "./ReportBuilderWizard";
import ReportCard from "./ReportCard";
import ReportFilters from "./ReportFilters";
import RoadmapReportViewer from "./RoadmapReportViewer";
import DataQualityReportViewer from "./DataQualityReportViewer";
import RelationshipReportViewer from "./RelationshipReportViewer";
import { createDevConsole } from "../../utils/devLogger";

const console = createDevConsole();

const ReportsDashboard = ({ user, embedded, registerNewReportAction }) => {
  const [myReports, setMyReports] = useState([]);
  const [sharedReports, setSharedReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [editingReport, setEditingReport] = useState(null); // Report being edited
  const [viewingReport, setViewingReport] = useState(null); // Report being viewed
  const [operationLoading, setOperationLoading] = useState({}); // Track operations by report ID
  const [successMessage, setSuccessMessage] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    owner: "",
    reportType: "",
    category: "",
    isFavorite: null,
  });
  
  // Track if we're currently loading to prevent multiple simultaneous loads
  const isLoadingRef = useRef(false);
  
  // Memoize filters to prevent unnecessary reloads when object reference changes but values don't
  const filtersString = useMemo(() => JSON.stringify(filters), [
    filters.search,
    filters.owner,
    filters.reportType,
    filters.category,
    filters.isFavorite,
  ]);

  useEffect(() => {
    loadReports();
  }, [filtersString]);

  const loadReports = async (
    showLoading = true,
    preserveReportId = null,
    excludeReportIds = []
  ) => {
    // Prevent multiple simultaneous loads
    if (isLoadingRef.current && showLoading) {
      return;
    }
    
    if (showLoading) {
      isLoadingRef.current = true;
      setLoading(true);
    }
    setError(null);
    try {
      // Load my reports
      const myResponse = await apiFetch("/api/reports/my");
      if (!myResponse.ok) throw new Error("Failed to load my reports");
      const myData = await myResponse.json();
      let loadedMyReports = myData.reports || [];

      // Exclude deleted reports (in case API still returns them due to timing/caching)
      if (excludeReportIds.length > 0) {
        const beforeCount = loadedMyReports.length;
        loadedMyReports = loadedMyReports.filter(
          (r) => !excludeReportIds.includes(r.id)
        );
        // Only log if we actually excluded something
        if (loadedMyReports.length < beforeCount) {
          console.log(
            "🚫 Excluded deleted reports from list:",
            excludeReportIds
          );
        }
      }

      // If we're preserving a specific report (e.g., just duplicated), check if it's in the list
      if (preserveReportId) {
        const currentReport = myReports.find((r) => r.id === preserveReportId);
        const inLoadedList = loadedMyReports.find(
          (r) => r.id === preserveReportId
        );
        if (currentReport && !inLoadedList) {
          // Report not in reloaded list, add it back (might be a timing issue)
          console.log(
            "🔄 Preserving duplicated report in list (not yet in API response):",
            preserveReportId
          );
          loadedMyReports = [currentReport, ...loadedMyReports];
        } else if (inLoadedList) {
          console.log(
            "✅ Duplicated report found in API response:",
            preserveReportId
          );
        }
      }
      setMyReports(loadedMyReports);

      // Load shared reports
      const sharedResponse = await apiFetch("/api/reports/shared");
      if (!sharedResponse.ok) throw new Error("Failed to load shared reports");
      const sharedData = await sharedResponse.json();
      let loadedSharedReports = sharedData.reports || [];

      // Exclude deleted reports from shared reports too
      if (excludeReportIds.length > 0) {
        loadedSharedReports = loadedSharedReports.filter(
          (r) => !excludeReportIds.includes(r.id)
        );
      }
      setSharedReports(loadedSharedReports);
    } catch (err) {
      console.error("Error loading reports:", err);
      setError(err.message || "Failed to load reports");
    } finally {
      if (showLoading) {
        setLoading(false);
        isLoadingRef.current = false;
      }
    }
  };

  const handleCreateReport = useCallback(() => {
    setEditingReport(null);
    setShowWizard(true);
  }, []);

  // Let the host page render the New Report button in its own panel
  // header (saves a full row here when embedded).
  useEffect(() => {
    if (!registerNewReportAction) return undefined;
    registerNewReportAction(handleCreateReport);
    return () => registerNewReportAction(null);
  }, [registerNewReportAction, handleCreateReport]);

  const handleEditReport = async (reportId) => {
    // Find the report in our lists
    let report = [...myReports, ...sharedReports].find(
      (r) => r.id === reportId
    );

    if (!report) {
      // If not in cache, fetch it
      try {
        const response = await apiFetch(`/api/reports/${reportId}`);
        const data = await response.json();
        if (data.success && data.report) {
          report = data.report;
        } else {
          alert("Failed to load report for editing");
          return;
        }
      } catch (err) {
        console.error("Error loading report:", err);
        alert("Failed to load report for editing");
        return;
      }
    }

    setEditingReport(report);
    setShowWizard(true);
  };

  const handleWizardClose = (saved, updatedReport = null) => {
    setShowWizard(false);
    setEditingReport(null);

    if (saved) {
      loadReports();

      // If saved with "Save & Run", open the report viewer
      if (updatedReport) {
        setViewingReport(updatedReport);
      }
    }
  };

  const handleRunReport = (reportId) => {
    // Find the report in our lists
    const report = [...myReports, ...sharedReports].find(
      (r) => r.id === reportId
    );
    if (report) {
      setViewingReport(report);
    } else {
      // If not in cache, fetch it
      apiFetch(`/api/reports/${reportId}`)
        .then((response) => response.json())
        .then((data) => {
          if (data.success && data.report) {
            setViewingReport(data.report);
          } else {
            alert("Failed to load report details");
          }
        })
        .catch((err) => {
          console.error("Error loading report:", err);
          alert("Failed to load report details");
        });
    }
  };

  const handleCloseViewer = () => {
    setViewingReport(null);
    // Reload reports to update run count
    loadReports(false);
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm("Are you sure you want to delete this report?")) {
      return;
    }

    // If the deleted report is currently being viewed, close the viewer first
    if (viewingReport && viewingReport.id === reportId) {
      setViewingReport(null);
    }

    // Set loading state for this specific report
    setOperationLoading((prev) => ({ ...prev, [reportId]: "deleting" }));

    try {
      const response = await apiFetch(`/api/reports/${reportId}`, {
        method: "DELETE",
      });

      // Handle 404 gracefully - report is already deleted
      if (response.status === 404) {
        // Report already deleted, just remove from UI
        const deletedReport =
          myReports.find((r) => r.id === reportId) ||
          sharedReports.find((r) => r.id === reportId);
        const reportName = deletedReport?.name || "Report";
        
        setMyReports((prev) => prev.filter((r) => r.id !== reportId));
        setSharedReports((prev) => prev.filter((r) => r.id !== reportId));
        
        setSuccessMessage(`Report "${reportName}" deleted successfully`);
        setTimeout(() => setSuccessMessage(null), 3000);
        return;
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Failed to delete report" }));
        throw new Error(error.message || "Failed to delete report");
      }

      // Get report name for success message before removing
      const deletedReport =
        myReports.find((r) => r.id === reportId) ||
        sharedReports.find((r) => r.id === reportId);
      const reportName = deletedReport?.name || "Report";

      // Optimistic update: Remove from UI immediately
      setMyReports((prev) => prev.filter((r) => r.id !== reportId));
      setSharedReports((prev) => prev.filter((r) => r.id !== reportId));

      // Show success message
      setSuccessMessage(`Report "${reportName}" deleted successfully`);
      setTimeout(() => setSuccessMessage(null), 3000);

      // No need to reload - optimistic update is sufficient
      // The next natural reload (from filters change or manual refresh) will sync with server
    } catch (err) {
      console.error("Error deleting report:", err);
      
      // Only show error if it's not a 404 (which we handle above)
      if (!err.message.includes("404") && !err.message.includes("Not Found")) {
        setError(`Failed to delete report: ${err.message}`);
        setTimeout(() => setError(null), 5000);
        
        // Reload to restore state if optimistic update was wrong
        // But exclude the report ID in case deletion actually succeeded
        await loadReports(false, null, [reportId]);
      } else {
        // 404 error - report already deleted, just remove from UI
        setMyReports((prev) => prev.filter((r) => r.id !== reportId));
        setSharedReports((prev) => prev.filter((r) => r.id !== reportId));
      }
    } finally {
      setOperationLoading((prev) => {
        const next = { ...prev };
        delete next[reportId];
        return next;
      });
    }
  };

  const handleToggleFavorite = async (reportId) => {
    // Set loading state for this specific report
    setOperationLoading((prev) => ({ ...prev, [reportId]: "favoriting" }));

    try {
      const response = await apiFetch(`/api/reports/${reportId}/favorite`, {
        method: "POST",
      });

      if (!response.ok) {
        throw new Error("Failed to toggle favorite");
      }

      const result = await response.json();
      const updatedReport = result.report;

      // Optimistic update: Update favorite status immediately
      setMyReports((prev) =>
        prev.map((r) => (r.id === reportId ? updatedReport : r))
      );
      setSharedReports((prev) =>
        prev.map((r) => (r.id === reportId ? updatedReport : r))
      );

      // Reload reports to ensure consistency (without showing full loading screen)
      await loadReports(false);
    } catch (err) {
      console.error("Error toggling favorite:", err);
      setError(`Failed to toggle favorite: ${err.message}`);
      setTimeout(() => setError(null), 5000);
      // Reload to restore state (without showing loading screen)
      await loadReports(false);
    } finally {
      setOperationLoading((prev) => {
        const next = { ...prev };
        delete next[reportId];
        return next;
      });
    }
  };

  const handleDuplicateReport = async (reportId) => {
    // Set loading state for this specific report
    setOperationLoading((prev) => ({ ...prev, [reportId]: "duplicating" }));

    try {
      const response = await apiFetch(`/api/reports/${reportId}/duplicate`, {
        method: "POST",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to duplicate report");
      }

      const result = await response.json();
      const duplicatedReport = result.report;

      if (!duplicatedReport) {
        throw new Error("Duplicated report not returned from server");
      }

      console.log(
        "✅ Report duplicated:",
        duplicatedReport.id,
        duplicatedReport.name
      );

      // Show success message
      setSuccessMessage(
        `Report "${duplicatedReport.name || "Report"}" duplicated successfully`
      );
      setTimeout(() => setSuccessMessage(null), 3000);

      // Optimistic update: Add to UI immediately (will be in "My Reports")
      setMyReports((prev) => {
        // Check if it's already in the list (shouldn't be, but just in case)
        const exists = prev.some((r) => r.id === duplicatedReport.id);
        if (exists) {
          console.log(
            "⚠️ Duplicated report already in list, skipping optimistic update"
          );
          return prev; // Don't add duplicate
        }
        console.log("➕ Adding duplicated report to list optimistically");
        return [duplicatedReport, ...prev];
      });

      // Reload reports after a short delay to ensure database is updated
      // This ensures we get the full report data with all relationships
      // Pass the duplicated report ID to preserve it if it's not in the reloaded list
      setTimeout(async () => {
        await loadReports(false, duplicatedReport.id);
      }, 500);
    } catch (err) {
      console.error("Error duplicating report:", err);
      setError(`Failed to duplicate report: ${err.message}`);
      setTimeout(() => setError(null), 5000);
      // Reload to restore state (without showing loading screen)
      await loadReports(false);
    } finally {
      setOperationLoading((prev) => {
        const next = { ...prev };
        delete next[reportId];
        return next;
      });
    }
  };

  // Filter reports based on current filters
  const filterReports = (reports) => {
    return reports.filter((report) => {
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch =
          report.name.toLowerCase().includes(searchLower) ||
          (report.description &&
            report.description.toLowerCase().includes(searchLower));
        if (!matchesSearch) return false;
      }

      if (filters.owner && report.created_by !== filters.owner) {
        return false;
      }

      if (filters.reportType && report.report_type !== filters.reportType) {
        return false;
      }

      if (filters.category && report.category !== filters.category) {
        return false;
      }

      if (
        filters.isFavorite !== null &&
        report.is_favorite !== filters.isFavorite
      ) {
        return false;
      }

      return true;
    });
  };

  const filteredMyReports = filterReports(myReports);
  const filteredSharedReports = filterReports(sharedReports);

  if (showWizard) {
    return (
      <ReportBuilderWizard
        user={user}
        onClose={handleWizardClose}
        editingReport={editingReport}
      />
    );
  }

  if (viewingReport) {
    // Route to the appropriate viewer based on report type
    if (viewingReport.report_type === "data-quality") {
      return (
        <DataQualityReportViewer
          reportId={viewingReport.id}
          report={viewingReport}
          onClose={handleCloseViewer}
        />
      );
    }

    if (viewingReport.report_type === "relationship") {
      return (
        <RelationshipReportViewer
          reportId={viewingReport.id}
          report={viewingReport}
          onClose={handleCloseViewer}
        />
      );
    }

    // Default to RoadmapReportViewer for roadmap and other types
    return (
      <RoadmapReportViewer
        reportId={viewingReport.id}
        report={viewingReport}
        onClose={handleCloseViewer}
      />
    );
  }

  const rootClass = embedded
    ? "w-full min-h-0 p-4 md:p-6"
    : "min-h-screen bg-gray-900 p-6";

  const newReportBtnClass = embedded
    ? "px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors flex items-center gap-2 text-sm"
    : "px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors flex items-center gap-2";

  const newReportBtn = (
    <button
      type="button"
      onClick={handleCreateReport}
      className={newReportBtnClass}
    >
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 4v16m8-8H4"
        />
      </svg>
      New Report
    </button>
  );

  return (
    <div className={rootClass}>
      <div className="max-w-7xl mx-auto">
        {embedded ? (
          // Host page renders New Report in its panel header via
          // registerNewReportAction; only fall back to a local row if
          // no host registered a handler.
          registerNewReportAction ? null : (
            <div className="mb-4 flex justify-end">{newReportBtn}</div>
          )
        ) : (
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Reports</h1>
              <p className="text-gray-400">
                Create, manage, and share custom reports for your enterprise
                architecture
              </p>
            </div>
            {newReportBtn}
          </div>
        )}

        {/* Filters */}
        <ReportFilters filters={filters} onFiltersChange={setFilters} />

        {/* Success Message */}
        {successMessage && (
          <div className="mb-4 bg-green-900/50 border border-green-700 rounded-lg p-4 text-green-200 flex items-center justify-between">
            <span>{successMessage}</span>
            <button
              onClick={() => setSuccessMessage(null)}
              className="text-green-400 hover:text-green-300"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <svg
                className="animate-spin w-12 h-12 text-blue-500 mx-auto mb-4"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <p className="text-gray-400">Loading reports...</p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-900/50 border border-red-700 rounded-lg p-4 text-red-200">
            Error: {error}
          </div>
        ) : (
          <>
            {/* My Reports Section */}
            <section className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-white">
                  My Reports ({filteredMyReports.length})
                </h2>
              </div>

              {filteredMyReports.length === 0 ? (
                <div className="bg-gray-800 rounded-lg border border-gray-700 p-8 text-center">
                  <p className="text-gray-400 mb-4">
                    {myReports.length === 0
                      ? "You haven't created any reports yet."
                      : "No reports match your filters."}
                  </p>
                  {myReports.length === 0 && (
                    <button
                      onClick={handleCreateReport}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                      Create Your First Report
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredMyReports.map((report) => (
                    <ReportCard
                      key={report.id}
                      report={report}
                      isOwner={true}
                      onDelete={handleDeleteReport}
                      onToggleFavorite={handleToggleFavorite}
                      onDuplicate={handleDuplicateReport}
                      onRun={handleRunReport}
                      onEdit={handleEditReport}
                      isDeleting={operationLoading[report.id] === "deleting"}
                      isDuplicating={
                        operationLoading[report.id] === "duplicating"
                      }
                    />
                  ))}
                </div>
              )}
            </section>

            {/* Shared Reports Section */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-white">
                  Shared Reports ({filteredSharedReports.length})
                </h2>
              </div>

              {filteredSharedReports.length === 0 ? (
                <div className="bg-gray-800 rounded-lg border border-gray-700 p-8 text-center">
                  <p className="text-gray-400">
                    {sharedReports.length === 0
                      ? "No reports have been shared with you yet."
                      : "No shared reports match your filters."}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredSharedReports.map((report) => (
                    <ReportCard
                      key={report.id}
                      report={report}
                      isOwner={false}
                      onToggleFavorite={handleToggleFavorite}
                      onRun={handleRunReport}
                      onDuplicate={handleDuplicateReport}
                      isDuplicating={
                        operationLoading[report.id] === "duplicating"
                      }
                    />
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportsDashboard;
